import { useState, type FormEvent } from 'react';
import {
  DialogActions,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Button,
} from '@mui/material';
import { EDIT_ROLE_MODAL_LABELS as LABELS, ROLE_LABELS } from '../../../../utils/labels';
import AppDialog from '../../../../components/AppDialog';
import { useModalHistory } from '../../../../utils/useModalHistory';
import SpinnerButton from '../../../../components/SpinnerButton';
import type { MemberRole } from '../../../../types/roles';
import type { OrgMember } from '../../types';

interface EditRoleModalProps {
  member: OrgMember;
  onClose: () => void;
  onSave: (role: MemberRole) => Promise<boolean>;
}

export default function EditRoleModal({ member, onClose, onSave }: EditRoleModalProps) {
  useModalHistory(onClose);
  const [role, setRole] = useState<MemberRole>(member.role);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    const ok = await onSave(role);
    setSaving(false);
    if (ok) onClose();
  };

  return (
    <AppDialog
      open
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      title={LABELS.title}
      confirmLabel={LABELS.save}
      confirmFormId="edit-role-form"
      confirmDisabled={saving}
      confirmLoading={saving}
      testId="edit-role-modal"
    >
      <form id="edit-role-form" onSubmit={handleSubmit} noValidate>
        <DialogContent className="flex flex-col gap-4">
          <FormControl fullWidth className="mt-2">
            <InputLabel>{LABELS.roleLabel}</InputLabel>
            <Select
              label={LABELS.roleLabel}
              value={role}
              onChange={(e) => setRole(e.target.value as MemberRole)}
              inputProps={{ 'aria-label': LABELS.roleLabel }}
            >
              {(Object.entries(ROLE_LABELS) as [MemberRole, string][]).map(([key, label]) => (
                <MenuItem key={key} value={key} data-testid={`option-role-${key}`}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="inherit" data-testid="cancel-button">{LABELS.cancel}</Button>
          <SpinnerButton type="submit" variant="contained" loading={saving} data-testid="save-button">
            {LABELS.save}
          </SpinnerButton>
        </DialogActions>
      </form>
    </AppDialog>
  );
}
